import BigNumber from 'bignumber.js';
import { ReactNode, useMemo } from 'react';

import { Trade, TradeSide } from '#/api/trades';
import { Market } from '#/api/markets';

import { useFuturesActiveMarketStoreView } from '#/features/perpetuals/active-market-context';
import * as MarketFn from '#/features/market/functions';

import createSafeContext from '#/utils/createSafeContext';
import { timeFormat } from '#/utils/date';
import { LiveConnectionBaseState } from '#/utils/useLiveConnection';
import { TradesStoreView, useTradesStore } from './trades-store-context';

export interface TradeTapeView extends LiveConnectionBaseState {
  readonly trades: readonly TradeView[];
}

export const [useTradeTrapeView, TradeTapeViewProviderInternal] =
  createSafeContext<TradeTapeView>('TradeTapeView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function TradeTapeViewProvider(props: ProviderProps) {
  const storeView = useTradesStore();
  const { activeMarket } = useFuturesActiveMarketStoreView();

  const view = useMemo(
    () => createView(storeView, activeMarket),
    [storeView, activeMarket],
  );

  return (
    <TradeTapeViewProviderInternal value={view}>
      {props.children}
    </TradeTapeViewProviderInternal>
  );
}

export interface TradeView {
  readonly id: string;
  readonly size: string;
  readonly price: string;
  readonly createdTime: string;
  readonly side: TradeSide;

  // Derived:
  readonly sizeRelativePercentage: string;
}

const emptyTradeTapeView = (store: TradesStoreView): TradeTapeView => ({
  status: store.status,
  error: store.error,
  trades: [],
});

export function createView(
  store: TradesStoreView,
  activeMarket: Market | null,
): TradeTapeView {
  if (activeMarket == null) return emptyTradeTapeView(store);
  const allTrades = store.getAllTrades();

  const totalSize = allTrades.reduce(
    (acc, trade) => acc.plus(trade.size),
    new BigNumber(0),
  );

  const trades = allTrades.map((trade) =>
    tradeView(trade, totalSize, activeMarket),
  );

  return {
    status: store.status,
    error: store.error,
    trades,
  };
}

function tradeView(
  trade: Trade,
  totalSize: BigNumber,
  activeMarket: Market,
): TradeView {
  const baseCurrencyDecimalPlaces =
    MarketFn.baseCurrencyDecimalPlaces(activeMarket);
  const quoteCurrencyDecimalPlaces =
    MarketFn.quoteCurrencyDecimalPlaces(activeMarket);

  const size = trade.size.toFormat(baseCurrencyDecimalPlaces);

  const sizeRelativePercentage = trade.size
    .dividedBy(totalSize)
    .multipliedBy(100)
    .toFixed(0);

  const price = trade.price.toFormat(quoteCurrencyDecimalPlaces);

  const createdTime = timeFormat(trade.created_at);

  return {
    id: trade.id,
    side: trade.side,
    size,
    sizeRelativePercentage,
    price,
    createdTime,
  };
}
